import React, { useState } from 'react';
import { Trash2, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { useToast } from '@/shared/hooks/use-toast';
import {
  useCreateNoteEntry,
  useDeleteAttachment,
  useDeleteNoteEntry,
  useUploadAttachments,
} from '../hooks';
import type { DailyLog, NoteEntry, NoteEntryDraft } from '../types';
import { AttachmentsCell } from './AttachmentsCell';

interface NotesSectionProps {
  projectId: number;
  dailyLog: DailyLog;
  expanded: boolean;
  onToggle: () => void;
}

const blankDraft = (): NoteEntryDraft => ({
  is_issue: false,
  location: '',
  comments: '',
});

export const NotesSection: React.FC<NotesSectionProps> = ({
  projectId,
  dailyLog,
  expanded,
  onToggle,
}) => {
  const { toast } = useToast();
  const [draft, setDraft] = useState<NoteEntryDraft>(blankDraft);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const createMutation = useCreateNoteEntry();
  const deleteMutation = useDeleteNoteEntry();
  const uploadMutation = useUploadAttachments(projectId);
  const deleteAttachmentMutation = useDeleteAttachment();

  const isCreating = createMutation.isPending || uploadMutation.isPending;

  const handleCreate = async () => {
    if (!draft.comments.trim() && !draft.location.trim()) {
      toast({
        title: 'Empty note',
        description: 'Add a comment or location before saving.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const created = await createMutation.mutateAsync({
        dailyLogId: dailyLog.id,
        draft,
      });
      if (pendingFiles.length > 0) {
        await uploadMutation.mutateAsync({
          files: pendingFiles,
          parent: { kind: 'note', id: created.id },
        });
      }
      setDraft(blankDraft());
      setPendingFiles([]);
    } catch (error) {
      toast({
        title: 'Failed to create note',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (entry: NoteEntry) => {
    const confirmed = window.confirm('Remove this note?');
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(entry.id);
    } catch (error) {
      toast({
        title: 'Failed to delete note',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUploadToExisting = async (entry: NoteEntry, files: File[]) => {
    try {
      await uploadMutation.mutateAsync({
        files,
        parent: { kind: 'note', id: entry.id },
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="rounded-md border bg-card">
      <header
        className="flex cursor-pointer items-center justify-between px-4 py-3"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              expanded ? '' : '-rotate-90'
            }`}
          />
          <h2 className="text-base font-semibold">Notes</h2>
        </div>
        <div className="text-xs text-muted-foreground">
          {dailyLog.note_entries.length} Note
          {dailyLog.note_entries.length === 1 ? '' : 's'}
        </div>
      </header>

      {expanded && (
        <div className="overflow-x-auto border-t">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Issue?</TableHead>
                <TableHead className="min-w-[180px]">Location</TableHead>
                <TableHead className="min-w-[240px]">Comments</TableHead>
                <TableHead className="min-w-[220px]">Attachments</TableHead>
                <TableHead className="min-w-[140px]">Created By</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {dailyLog.note_entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <Checkbox checked={entry.is_issue} disabled />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.location || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.comments || '—'}
                  </TableCell>
                  <TableCell>
                    <AttachmentsCell
                      attachments={entry.attachments}
                      isUploading={uploadMutation.isPending}
                      onAttach={(files) => handleUploadToExisting(entry, files)}
                      onRemoveExisting={(uuid) =>
                        deleteAttachmentMutation.mutate(uuid)
                      }
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {entry.created_by
                      ? `${entry.created_by.first_name || ''} ${entry.created_by.last_name || ''}`.trim() ||
                        entry.created_by.email
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(entry)}
                      aria-label="Delete note"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {/* Draft row */}
              <TableRow className="bg-muted/20 hover:bg-muted/20">
                <TableCell>
                  <Checkbox
                    checked={draft.is_issue}
                    onCheckedChange={(checked) =>
                      setDraft((prev) => ({ ...prev, is_issue: !!checked }))
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    placeholder="Select a Location"
                    value={draft.location}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, location: event.target.value }))
                    }
                    className="h-9"
                  />
                </TableCell>
                <TableCell>
                  <Textarea
                    placeholder="Add comment"
                    value={draft.comments}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, comments: event.target.value }))
                    }
                    className="min-h-[36px] resize-none py-1.5"
                    rows={1}
                  />
                </TableCell>
                <TableCell>
                  <AttachmentsCell
                    attachments={[]}
                    pendingFiles={pendingFiles}
                    onAttach={(files) =>
                      setPendingFiles((prev) => [...prev, ...files])
                    }
                    onRemovePending={(index) =>
                      setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">—</TableCell>
                <TableCell>
                  <Button
                    type="button"
                    onClick={handleCreate}
                    disabled={isCreating}
                    className="h-9"
                  >
                    {isCreating && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Create
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
};
